import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { authenticatedFetch } from "@/lib/auth";

interface TicketActionModalProps {
    isOpen: boolean;
    onClose: () => void;
    ticketId: number;
    ticketNumber: string;
    currentStatus: string;
    onActionComplete: () => void;
}

type ActionType = "respond" | "ask_info" | "close" | "view" | null;

export const TicketActionModal = ({
    isOpen,
    onClose,
    ticketId,
    ticketNumber,
    currentStatus,
    onActionComplete,
}: TicketActionModalProps) => {
    const [selectedAction, setSelectedAction] = useState<ActionType>(null);
    const [message, setMessage] = useState("");
    const [timeSpent, setTimeSpent] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [resolution, setResolution] = useState("");

    const handleClose = () => {
        setSelectedAction(null);
        setMessage("");
        setTimeSpent(0);
        setError("");
        setResolution("");
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedAction || !message.trim()) {
            setError("Mensagem é obrigatória");
            return;
        }

        setIsSubmitting(true);
        setError("");

        try {
            let payload: any = {};

            if (selectedAction === "respond") {
                payload = {
                    type: 'solution',
                    message: message,
                    metadata: {
                        time_spent: timeSpent || 0,
                    }
                };
            } else if (selectedAction === "ask_info") {
                payload = {
                    type: 'info_request',
                    message: message,
                };
            } else if (selectedAction === "close") {
                payload = {
                    type: 'status_change',
                    message: resolution || message || 'Ticket fechado',
                    metadata: {
                        old_status: currentStatus,
                        new_status: 'fechado',
                        reason: 'resolved'
                    }
                };
            }

            const response = await authenticatedFetch(`/api/tickets/${ticketId}/interact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || "Erro ao executar ação");
            }

            handleClose();
            onActionComplete();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erro desconhecido");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getActionConfig = (action: ActionType) => {
        switch (action) {
            case "respond":
                return {
                    title: "Responder Cliente",
                    description: "Envie uma resposta e o ticket passará para 'Aguardando Cliente'",
                    icon: CheckCircle2,
                    color: "text-blue-600",
                };
            case "ask_info":
                return {
                    title: "Solicitar Informações",
                    description: "Peça mais informações ao cliente antes de resolver",
                    icon: AlertCircle,
                    color: "text-orange-600",
                };
            case "close":
                return {
                    title: "Fechar Ticket",
                    description: "Feche o ticket diretamente (sem resposta ao cliente)",
                    icon: CheckCircle2,
                    color: "text-green-600",
                };
            default:
                return {
                    title: "",
                    description: "",
                    icon: Info,
                    color: "",
                };
        }
    };

    const config = getActionConfig(selectedAction);
    const Icon = config.icon;

    // Se não há ação selecionada, mostrar seleção
    if (!selectedAction) {
        return (
            <Dialog open={isOpen && !selectedAction} onOpenChange={handleClose}>
                <DialogContent className="max-w-md" data-testid="action-modal">
                    <DialogHeader>
                        <DialogTitle>
                            {ticketNumber} - Próxima Ação
                        </DialogTitle>
                        <DialogDescription>
                            Escolha qual ação deseja executar para este ticket
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3" data-testid="actions-dropdown">
                        {/* Responder */}
                        <button
                            data-testid="action-respond"
                            onClick={() => setSelectedAction("respond")}
                            className="w-full p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition text-left"
                        >
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-gray-900">Responder Cliente</p>
                                    <p className="text-sm text-gray-600">
                                        Envie uma solução ou informação
                                    </p>
                                    <Badge className="mt-2 bg-blue-100 text-blue-800">
                                        Estado: WAITING_CLIENT
                                    </Badge>
                                </div>
                            </div>
                        </button>

                        {/* Pedir Informações */}
                        <button
                            data-testid="action-request-info"
                            onClick={() => setSelectedAction("ask_info")}
                            className="w-full p-4 border rounded-lg hover:bg-orange-50 hover:border-orange-300 transition text-left"
                        >
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-gray-900">Solicitar Informações</p>
                                    <p className="text-sm text-gray-600">
                                        Peça dados adicionais para resolver
                                    </p>
                                    <Badge className="mt-2 bg-orange-100 text-orange-800">
                                        Estado: WAITING_CLIENT_INFO
                                    </Badge>
                                </div>
                            </div>
                        </button>

                        {/* Fechar */}
                        <button
                            data-testid="action-close"
                            onClick={() => setSelectedAction("close")}
                            className="w-full p-4 border rounded-lg hover:bg-green-50 hover:border-green-300 transition text-left"
                        >
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-gray-900">Fechar Ticket</p>
                                    <p className="text-sm text-gray-600">
                                        Finalize sem resposta (cliente parou)
                                    </p>
                                    <Badge className="mt-2 bg-green-100 text-green-800">
                                        Estado: CLOSED
                                    </Badge>
                                </div>
                            </div>
                        </button>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={handleClose} className="flex-1">
                            Cancelar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Se há ação selecionada, mostrar formulário
    return (
        <Dialog open={isOpen && !!selectedAction} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl" data-testid="action-modal">
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <Icon className={`w-6 h-6 ${config.color} mt-1 flex-shrink-0`} />
                        <div>
                            <DialogTitle>{config.title}</DialogTitle>
                            <DialogDescription>{config.description}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Info do Ticket */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-900">
                            Ticket: <span className="font-mono">{ticketNumber}</span>
                        </p>
                    </div>

                    {/* Campo: Mensagem */}
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Mensagem *
                        </label>
                        <Textarea
                            data-testid="message-textarea"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={
                                selectedAction === "respond"
                                    ? "Descreva a solução ou informações..."
                                    : selectedAction === "ask_info"
                                        ? "Explique que informações adicionais você precisa..."
                                        : "Motivo do fechamento (opcional)..."
                            }
                            className="min-h-24"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {message.length}/500 caracteres
                        </p>
                    </div>

                    {/* Campo: Tempo Gasto (apenas responder) */}
                    {selectedAction === "respond" && (
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Tempo Gasto (minutos)
                            </label>
                            <Input
                                data-testid="time-spent-input"
                                type="number"
                                value={timeSpent}
                                onChange={(e) => setTimeSpent(parseInt(e.target.value) || 0)}
                                min="0"
                                max="999"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Tempo total dedicado a este ticket
                            </p>
                        </div>
                    )}

                    {/* Campo: Resolução (apenas fechar) */}
                    {selectedAction === "close" && (
                        <>
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Descrição da Resolução (opcional)
                                </label>
                                <Textarea
                                    data-testid="resolution-textarea"
                                    value={resolution}
                                    onChange={(e) => setResolution(e.target.value)}
                                    placeholder="Adicione detalhes sobre como foi resolvido..."
                                    className="min-h-20"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Motivo do Fechamento
                                </label>
                                <select
                                    data-testid="close-reason-select"
                                    className="w-full px-3 py-2 border rounded-md"
                                    defaultValue="resolved"
                                >
                                    <option value="resolved">Resolvido</option>
                                    <option value="duplicate">Duplicado</option>
                                    <option value="wont_fix">Não será corrigido</option>
                                    <option value="invalid">Inválido</option>
                                </select>
                            </div>
                        </>
                    )}

                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {/* Sucesso */}
                    {!error && isSubmitting && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2" data-testid="success-message">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-green-800">Ação executada com sucesso!</p>
                        </div>
                    )}

                    {/* Aviso */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                        <p className="font-medium mb-1">⚠️ Aviso</p>
                        <p>
                            {selectedAction === "respond"
                                ? "O cliente receberá uma notificação sobre sua resposta"
                                : selectedAction === "ask_info"
                                    ? "O cliente será notificado e poderá enviar as informações solicitadas"
                                    : "O ticket será fechado imediatamente. Você não poderá reabrê-lo facilmente."}
                        </p>
                    </div>
                </div>

                {/* Botões */}
                <div className="flex gap-2 pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={() => setSelectedAction(null)}
                        disabled={isSubmitting}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        data-testid="confirm-action-btn"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !message.trim()}
                        className="flex-1"
                    >
                        {isSubmitting ? "Enviando..." : "Confirmar"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
